import React, { useState, useEffect, useMemo } from "react"
import { Row, Col } from "reactstrap"
import { AvForm, AvField, AvRadioGroup, AvRadio } from "availity-reactstrap-validation"
import api from "API/Api"
import jsPDF from "jspdf"
import "jspdf-autotable"
import Select from "react-select"
import loader from "./../../assets/loading/loader.gif"

const allDisplayParams = [
  { value: "transferTypeLabel", label: "Transfer Type" },
  { value: "companyName", label: "Company" },
  { value: "plantName", label: "Plant" },
  { value: "chillingCenterName", label: "Chilling Center" },
  { value: "loading_location_name", label: "Loading Location" },
  { value: "unloading_location_name", label: "Unloading Location" },
  { value: "locationTypeLabel", label: "Location Type" },
  { value: "vehicleNumber", label: "Vehicle Number" },
  { value: "compartmentNumber", label: "Vehicle Compartment Number" },
  { value: "driverName", label: "Driver Name" },
  { value: "driverMobile", label: "Driver Mobile" },
  { value: "transferDate", label: "Transfer Date" },
  { value: "loadingInTime", label: "Loading In Time" },
  { value: "loadingOutTime", label: "Loading Out Time" },
  { value: "unloadingInTime", label: "Unloading In Time" },
  { value: "unloadingOutTime", label: "Unloading Out Time" },
  { value: "quantity", label: "Quantity (Ltrs)" },
  { value: "fat", label: "FAT" },
  { value: "snf", label: "SNF" },
  { value: "clr", label: "CLR" },
  { value: "lacto", label: "Lacto" },
  { value: "acidity", label: "Acidity" },
  { value: "milkType", label: "Milk Type" },
  { value: "statusLabel", label: "Status" },
  { value: "business_vendor_id", label: "Business Vendor" },
  { value: "remarks", label: "Remarks" },
  { value: "created_at", label: "Created At" },
]

const MilkTransferReport = () => {
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [results, setResults] = useState([])
  const [selectedParams, setSelectedParams] = useState([])
  const [orderParams, setOrderParams] = useState(false)
  const [search, setSearch] = useState("")
  const [transferType, setTransferType] = useState("internal")
  const [companies, setCompanies] = useState([])
  const [vendors, setVendors] = useState([])

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    try {
      const [coResp] = await Promise.all([api.get("companies")])
      const coData = Array.isArray(coResp.data.data) ? coResp.data.data : coResp.data.data?.data || []
      setCompanies(coData)
    } catch (e) { console.error(e) }
  }

  // Load bulk vendors when transfer type changes to outside
  useEffect(() => {
    if (transferType === "outside") {
      api.get("bulk-vendors").then(r => {
        setVendors(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
      }).catch(() => {})
    }
  }, [transferType])

  const showCompany = companies.length > 1

  const handleSubmit = async (e, v) => {
    setLoading(true)
    try {
      v.transferType = transferType
      if (companies.length === 1) v.company = companies[0].id
      const resp = await api.post("reports/milk-transfer", v)
      if (resp.data.success) { setResults(resp.data.data || []); setSubmitted(true) }
    } catch (err) { console.error(err) }
    setLoading(false)
  }

  const filtered = useMemo(() => {
    if (!search) return results
    const s = search.toLowerCase()
    return results.filter(r => Object.values(r).some(v => v && String(v).toLowerCase().includes(s)))
  }, [results, search])

  const exportCSV = () => {
    const cols = selectedParams
    if (!filtered.length || !cols.length) return
    const headers = cols.map(c => c.label)
    const rows = filtered.map(r => cols.map(c => r[c.value] ?? ""))
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v || "").replace(/"/g, '""')}"`).join(",")).join("\n")
    const blob = new Blob([csv], { type: "text/csv" })
    const u = URL.createObjectURL(blob)
    const a = document.createElement("a"); a.href = u; a.download = "MilkTransferReport.csv"; a.click(); URL.revokeObjectURL(u)
  }

  const downloadPdf = () => {
    const pdf = new jsPDF("l")
    pdf.autoTable({ html: "#milkTransferTbl", styles: { fontSize: 7 } })
    pdf.save("MilkTransferReport.pdf")
  }

  const renderOrderParams = () => {
    if (!selectedParams.length) return <div style={{ color: "#94a3b8", fontSize: "12px", padding: "8px" }}>Select display parameters first</div>
    return (
      <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: "4px" }}>
        {selectedParams.map((p, i) => (
          <div key={p.value} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", borderBottom: "1px solid #f1f5f9", fontSize: "12px", background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
            <span>{p.label}</span>
            <i className="fas fa-arrows-alt-v" style={{ color: "#94a3b8" }}></i>
          </div>
        ))}
      </div>
    )
  }

  const cols = selectedParams

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader notToPrint">
        <Row><Col md={12}><h4>Milk Transfer Report</h4></Col></Row>
      </div>
      <div className="dashContent">
        <AvForm className="form-horizontal notToPrint" onValidSubmit={(e, v) => handleSubmit(e, v)}>
          {/* Row 1: Transfer Type, conditional Business Vendor, Operation Type, Period From, Period To */}
          <Row>
            <Col md={3}>
              <div className="mb-3">
                <label className="form-label">Transfer Type <span className="text-danger">*</span></label>
                <div style={{ display: "flex", gap: "20px", paddingTop: "6px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                    <input type="radio" name="transferTypeRadio" value="internal" checked={transferType === "internal"} onChange={() => setTransferType("internal")} />
                    <span>Internal Transfer</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                    <input type="radio" name="transferTypeRadio" value="outside" checked={transferType === "outside"} onChange={() => setTransferType("outside")} />
                    <span>Outside Sales</span>
                  </label>
                </div>
              </div>
            </Col>
            {transferType === "outside" && (
              <Col md={3}><div className="mb-3"><AvField type="select" name="businessVendor" label="Business Vendor"><option value="">All</option>{vendors.map((v, i) => <option key={i} value={v.id}>{v.vendorName}</option>)}</AvField></div></Col>
            )}
            <Col md={transferType === "outside" ? 2 : 3}>
              <div className="mb-3">
                <label className="form-label">Operation Type <span className="text-danger">*</span></label>
                <div style={{ display: "flex", gap: "20px", paddingTop: "6px" }}>
                  <AvRadioGroup inline name="operationType" required>
                    <AvRadio label="Loading" value="loading" />
                    <AvRadio label="Unloading" value="unloading" />
                  </AvRadioGroup>
                </div>
              </div>
            </Col>
            <Col md={transferType === "outside" ? 2 : 3}><div className="mb-3"><AvField type="date" name="periodFrom" label="Period From *" required /></div></Col>
            <Col md={transferType === "outside" ? 2 : 3}><div className="mb-3"><AvField type="date" name="periodTo" label="Period To *" required /></div></Col>
          </Row>

          {/* Row 2: Location Type, Status, Display Parameters, Order Parameters */}
          <Row>
            {showCompany && <Col md={3}><div className="mb-3"><AvField type="select" name="company" label="Company"><option value="">All</option>{companies.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>}
            <Col md={3}>
              <div className="mb-3">
                <AvField type="select" name="locationType" label="Location Type">
                  <option value="">All</option>
                  <option value="1">Chilling Center</option>
                  <option value="2">Plant</option>
                </AvField>
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3">
                <AvField type="select" name="status" label="Status">
                  <option value="">All</option>
                  <option value="10">Despatched</option>
                  <option value="20">Lost In Transit</option>
                  <option value="30">Completed</option>
                </AvField>
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3">
                <label>Display Parameters</label>
                <Select isMulti options={allDisplayParams} value={selectedParams} onChange={v => setSelectedParams(v || [])} placeholder="Nothing selected" className="basic-multi-select" classNamePrefix="select" />
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3" style={{ paddingTop: "28px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                  <input type="checkbox" checked={orderParams} onChange={e => setOrderParams(e.target.checked)} />
                  <span>Order Parameters</span>
                </label>
              </div>
            </Col>
          </Row>

          {/* Order Params panel */}
          {orderParams && (
            <Row>
              <Col md={4}><div className="mb-3"><label>Order Parameters</label>{renderOrderParams()}</div></Col>
            </Row>
          )}

          <Row>
            <Col md={12}><button type="submit" className="btn btn-primary"><i className="fas fa-check me-1"></i>Submit</button></Col>
          </Row>
        </AvForm>

        {loading && <div className="text-center py-4"><img className="loaderImg" src={loader} alt="Loading" /></div>}

        {submitted && !loading && (
          <>
            {cols.length === 0 ? (
              <div className="text-center py-4 text-muted">Please select display parameters and submit</div>
            ) : (
              <>
                <div className="notToPrint" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", marginTop: "16px", flexWrap: "wrap", gap: "8px" }}>
                  <div style={{ fontSize: "13px", color: "#64748b" }}>
                    Showing <strong>{filtered.length}</strong> records
                  </div>
                  <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    <input type="text" className="form-control form-control-sm" placeholder="Search..." style={{ width: "180px" }} value={search} onChange={e => setSearch(e.target.value)} />
                    <button className="btn btn-sm btn-outline-success" onClick={exportCSV} title="Export CSV"><i className="fas fa-file-csv"></i></button>
                    <button className="btn btn-sm btn-outline-danger" onClick={downloadPdf} title="Export PDF"><i className="fas fa-file-pdf"></i></button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => window.print()} title="Print"><i className="fas fa-print"></i></button>
                  </div>
                </div>
                <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
                  <table className="table table-sm table-hover table-bordered mb-0" id="milkTransferTbl" style={{ fontSize: "11px" }}>
                    <thead style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 1 }}>
                      <tr>
                        <th style={{ fontSize: "10px", color: "#64748b" }}>S.No</th>
                        {cols.map(c => <th key={c.value} style={{ fontSize: "10px", color: "#64748b", whiteSpace: "nowrap" }}>{c.label}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((r, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          {cols.map(c => <td key={c.value} style={{ whiteSpace: "nowrap" }}>{r[c.value] || "-"}</td>)}
                        </tr>
                      ))}
                      {filtered.length === 0 && <tr><td colSpan={cols.length + 1} className="text-center py-4 text-muted">No records found</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default MilkTransferReport
